// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { dumpObj, getDeferred, isNullOrUndefined, isString, objDefineProps, objFreeze, objIs, objKeys, perfNow } from "@nevware21/ts-utils";
import { _DYN_ATTRIBUTES, _DYN_ERROR_HANDLERS, _DYN_LENGTH, _DYN_MESSAGE, _DYN_PARENT_SPAN_CONTEXT, _DYN_SPAN_CONTEXT, _DYN_SPAN_ID, _DYN_START_TIME, _DYN_SUPPRESS_TRACING, _DYN_TRACE_ID } from "../../../__DynamicConstants";
import { STR_EMPTY, UNDEFINED_VALUE } from "../../../constants/InternalConstants";
import { isAttributeValue } from "../../../internal/attributeHelpers";
import { handleAttribError, handleNotImplemented, handleSpanError, handleWarn } from "../../../internal/handleErrors";
import { hrTime, hrTimeDuration, hrTimeToMilliseconds, millisToHrTime, timeInputToHrTime, zeroHrTime } from "../../../internal/timeHelpers";
import { setProtoTypeName, updateProtoTypeName } from "../../../utils/HelperFuncs";
import { addAttributes, createAttributeContainer } from "../../attribute/attributeContainer";
export function createSpan(spanCtx, orgName, kind) {
    var otelCfg = spanCtx.api.cfg;
    var perfStartTime = perfNow();
    var spanContext = spanCtx[_DYN_SPAN_CONTEXT /* @min:%2espanContext */];
    var attributes;
    var isEnded = false;
    var errorHandlers = otelCfg[_DYN_ERROR_HANDLERS /* @min:%2eerrorHandlers */] || {};
    var spanStartTime = getDeferred(function () {
        if (isNullOrUndefined(spanCtx[_DYN_START_TIME /* @min:%2estartTime */])) {
            return hrTime(perfStartTime);
        }
        return timeInputToHrTime(spanCtx[_DYN_START_TIME /* @min:%2estartTime */]);
    });
    var spanEndTime;
    var spanDuration;
    var spanStatus;
    // let links: IOTelLink[] = [];
    // let events: IOTelTimedEvent[] = [];
    var localDroppedAttributes = 0;
    var localContainer = null;
    // let droppedEvents = 0;
    // let droppedLinks = 0;
    var isRecording = spanCtx.isRecording !== false;
    if (otelCfg.traceCfg && otelCfg.traceCfg[_DYN_SUPPRESS_TRACING /* @min:%2esuppressTracing */]) {
        // Automatically disable the span from recording
        isRecording = false;
    }
    var spanName = orgName || STR_EMPTY;
    if (isRecording) {
        attributes = getDeferred(function () { return createAttributeContainer(otelCfg, spanName, spanCtx[_DYN_ATTRIBUTES /* @min:%2eattributes */]); });
    }
    function _handleIsEnded(operation, extraMsg) {
        if (isEnded) {
            handleSpanError(errorHandlers, "Span {traceID: " + spanContext[_DYN_TRACE_ID /* @min:%2etraceId */] + ", spanId: " + spanContext[_DYN_SPAN_ID /* @min:%2espanId */] + "} has ended - operation [" + operation + "] unsuccessful" + (extraMsg ? (" - " + extraMsg) : STR_EMPTY) + ".", spanName);
        }
        return isEnded;
    }
    function _toString() {
        return "ReadableSpan (\"" + spanName + "\")";
    }
    var theSpan = setProtoTypeName({
        spanContext: function () { return spanContext; },
        setAttribute: function (key, value) {
            var message;
            if (value !== null && !_handleIsEnded("setAttribute") && isRecording) {
                if (!key || key[_DYN_LENGTH /* @min:%2elength */] === 0) {
                    message = "Invalid attribute key: " + dumpObj(key);
                }
                else if (!isAttributeValue(value)) {
                    message = "Invalid attribute value: " + dumpObj(value);
                }
                if (message) {
                    handleAttribError(errorHandlers, message, key, value);
                    localDroppedAttributes++;
                }
                else if (attributes) {
                    attributes.v.set(key, value);
                }
                else {
                    localDroppedAttributes++;
                }
            }
            else {
                localDroppedAttributes++;
            }
            return theSpan;
        },
        setAttributes: function (attrs) {
            if (!_handleIsEnded("setAttributes") && isRecording && attributes) {
                addAttributes(attributes.v, attrs);
            }
            else {
                localDroppedAttributes += (objKeys(attrs)[_DYN_LENGTH /* @min:%2elength */] || 0);
            }
            return theSpan;
        },
        // addEvent: (name: string, attributesOrStartTime?: IOTelAttributes | OTelTimeInput, startTime?: OTelTimeInput) => {
        //     droppedEvents++;
        //     if(!_handleIsEnded("addEvent") && isRecording) {
        //         handleWarn(errorHandlers, "Span.addEvent: " + name + " not added - No events allowed");
        //     }
        //     return theSpan;
        // },
        // addLink: (link: any) => {
        //     droppedLinks++;
        //     if(!_handleIsEnded("addEvent") && isRecording) {
        //         handleWarn(errorHandlers, "Span.addLink: " + link + " not added - No links allowed");
        //     }
        //     return theSpan;
        // },
        // addLinks: (links: any[]) => {
        //     droppedLinks += links.length;
        //     if (!_handleIsEnded("addLinks") && isRecording) {
        //         handleWarn(errorHandlers, "Span.addLinks: " + links + " not added - No links allowed");
        //     }
        //     return theSpan;
        // },
        setStatus: function (newStatus) {
            if (!_handleIsEnded("setStatus")) {
                spanStatus = newStatus;
                if (!isNullOrUndefined(spanStatus) && !isNullOrUndefined(spanStatus[_DYN_MESSAGE /* @min:%2emessage */]) && !isString(spanStatus[_DYN_MESSAGE /* @min:%2emessage */])) {
                    spanStatus[_DYN_MESSAGE /* @min:%2emessage */] = dumpObj(spanStatus[_DYN_MESSAGE /* @min:%2emessage */]);
                }
            }
            return theSpan;
        },
        updateName: function (name) {
            if (!_handleIsEnded("updateName") && !objIs(spanName, name)) {
                spanName = name;
                updateProtoTypeName(theSpan, _toString());
            }
            return theSpan;
        },
        end: function (endTime) {
            var calcDuration;
            if (!_handleIsEnded("end", "You can only call end once")) {
                try {
                    if (!isNullOrUndefined(endTime)) {
                        // User provided an end time
                        spanEndTime = timeInputToHrTime(endTime);
                        spanDuration = hrTimeDuration(spanStartTime.v, spanEndTime);
                        calcDuration = hrTimeToMilliseconds(spanDuration);
                    }
                    else {
                        var perfEndTime = perfNow();
                        calcDuration = perfEndTime - perfStartTime;
                        spanDuration = millisToHrTime(calcDuration);
                        spanEndTime = hrTime(perfEndTime);
                    }
                    if (calcDuration < 0) {
                        handleWarn(errorHandlers, "Span.end: duration is negative - startTime > endTime. Setting duration to 0 ms");
                        spanDuration = zeroHrTime();
                        spanEndTime = spanStartTime.v;
                    }
                    // if (droppedEvents > 0) {
                    //     handleWarn(errorHandlers, "Droped " + droppedEvents + " events");
                    // }
                    // We don't mark as ended until after the onEnd callback to ensure that it can
                    // still read / change the span if required as well as ensuring that the returned
                    // value for isRecording is correct.
                    spanCtx.onEnd && spanCtx.onEnd(theSpan);
                }
                finally {
                    // Ensure we mark as ended even if the onEnd throws
                    isEnded = true;
                }
            }
        },
        isRecording: function () { return isRecording && !isEnded; },
        recordException: function (exception, time) {
            if (!_handleIsEnded("recordException")) {
                if (spanCtx.onException) {
                    spanCtx.onException(theSpan, exception, time);
                }
                else {
                    handleNotImplemented(errorHandlers, "Span.recordException: " + dumpObj(exception) + " not handled");
                }
            }
        }
    }, _toString());
    // Make the relevant properties dynamic (and read-only)
    objDefineProps(theSpan, {
        name: {
            g: function () { return spanName; }
        },
        kind: {
            v: kind || 0 /* eOTelSpanKind.INTERNAL */
        },
        startTime: {
            g: function () {
                return spanStartTime.v;
            }
        },
        endTime: {
            g: function () {
                return spanEndTime;
            }
        },
        status: {
            g: function () {
                return spanStatus || {
                    code: 0 /* eOTelSpanStatusCode.UNSET */
                };
            }
        },
        attributes: {
            g: function () {
                return attributes ? attributes.v[_DYN_ATTRIBUTES /* @min:%2eattributes */] : objFreeze({});
            }
        },
        attribContainer: {
            g: function () {
                if (!attributes && !localContainer) {
                    // Create an empty container and cache it for future use (for performance only)
                    localContainer = createAttributeContainer(otelCfg, spanName);
                }
                return attributes ? attributes.v : localContainer;
            }
        },
        links: {
            g: function () {
                return [];
            }
        },
        events: {
            g: function () {
                return [];
            }
        },
        duration: {
            g: function () {
                return spanDuration || zeroHrTime();
            }
        },
        ended: {
            g: function () {
                return isEnded;
            }
        },
        droppedAttributesCount: {
            g: function () {
                return attributes ? attributes.v.droppedAttributes : localDroppedAttributes;
            }
        },
        // droppedEventsCount: {
        //     g: () => {
        //         return droppedEvents;
        //     }
        // },
        // droppedLinksCount: {
        //     g: () => {
        //         return droppedLinks;
        //     }
        // },
        parentSpanContext: {
            l: getDeferred(function () {
                return spanCtx ? spanCtx[_DYN_PARENT_SPAN_CONTEXT /* @min:%2eparentSpanContext */] : UNDEFINED_VALUE;
            })
        },
        parentSpanId: {
            l: getDeferred(function () {
                var parentSpanId = UNDEFINED_VALUE;
                if (spanCtx) {
                    var parentSpanCtx = spanCtx[_DYN_PARENT_SPAN_CONTEXT /* @min:%2eparentSpanContext */];
                    parentSpanId = parentSpanCtx ? parentSpanCtx[_DYN_SPAN_ID /* @min:%2espanId */] : UNDEFINED_VALUE;
                }
                return parentSpanId;
            })
        }
    });
    return theSpan;
}
//# sourceMappingURL=span.js.map